import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../../lib/auth';

/**
 * Gates the /admin area on the system-admin flag.
 *
 * Client-side only, and only for presentation: every /admin API route is guarded server-side by
 * SystemAdminGuard, which reads the flag from the database per request. This exists so a
 * non-admin who follows a link gets an explanation instead of a console full of failed requests.
 */
export function AdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isSystemAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-200 dark:border-orange-900 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-sm text-ink-faint">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!isSystemAdmin) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <div className="w-12 h-12 rounded-xl bg-surface-muted flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={22} className="text-ink-faint" />
          </div>
          <p className="text-sm font-medium text-ink-secondary mb-1.5">Administrators only</p>
          <p className="text-xs text-ink-faint mb-4">
            This area manages accounts and feature access across the whole workspace. Your account
            doesn't have system-admin permission.
          </p>
          <Link to="/" className="text-[11px] text-orange-500 hover:text-orange-700 transition-colors">
            Back to workspace
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
